import React, { useState } from 'react'
import FlavoursTile from './FlavoursTile'
import cn from 'classnames'
import { FlavoursStepProps, Mapper, SkuMap } from '../../lib/type'
import { useTranslation } from 'next-i18next'
import {
    BundleItem,
    BundleItemOption,
    SimpleProduct
} from '@pylot-data/fwrdschema'
import { Maybe } from '@pylot-data/fwrdschema'

import { ConvertObjToArray } from '@corratech/pylot-custom-kit-flow/src/utils/ConvertObjToArray'
import { getFlavourAmount } from '@corratech/pylot-custom-kit-flow/src/utils/getFlavourAmount'
import fs from './Flavours.module.scss'
import FlavourAction from '@corratech/pylot-custom-kit-flow/src/Components/FlavoursStep/FlavourAction'

function getTabFlavours(mappedFlavours: Mapper[], currentTab: string) {
    const filteredFlavours = mappedFlavours.filter((flavour) => {
        switch (currentTab) {
            case 'all':
                return true
            case 'tobacco':
                return flavour.product?.is_tobacco_product === '1'
            case 'nicotine-free':
                return flavour.product?.is_tobacco_product === '0'
            default:
                return false
        }
    })

    return filteredFlavours.sort((a, b) => {
        if (
            a.product?.stock_status === 'OUT_OF_STOCK' &&
            b.product?.stock_status !== 'OUT_OF_STOCK'
        ) {
            return 1
        }
        if (
            a.product?.stock_status !== 'OUT_OF_STOCK' &&
            b.product?.stock_status === 'OUT_OF_STOCK'
        ) {
            return -1
        }
        return 0
    })
}

function aggregateUniqueUIDsBySKU(data: Maybe<BundleItem>[]) {
    const skuMap: SkuMap = {}

    data?.forEach((item, index) => {
        item?.options?.forEach((option) => {
            const sku = String(option?.product?.sku)
            if (option?.product?.sku && sku && !skuMap?.[sku]) {
                skuMap[sku] = {
                    uids: [option?.uid],
                    count: 1,
                    product: option?.product as Maybe<SimpleProduct>,
                    pods: [index]
                }
            }

            /**
             * Check if UID already exists to avoid duplicates
             */
            if (
                option?.product?.sku &&
                !skuMap?.[sku]?.uids?.includes(String(option?.uid))
            ) {
                skuMap[sku].uids.push(String(option?.uid))
                skuMap[sku].count++
                skuMap?.[sku]?.pods?.push(index)
            }
        })
    })
    return ConvertObjToArray(skuMap)
}
export const FlavoursStep = ({
    product,
    setStep,
    step,
    selectedOptions,
    setSelectedOptions,
    addProductHandler,
    isAddToCartInProgress,
    isFavourite
}: FlavoursStepProps): JSX.Element => {
    const [currentTab, setTab] = useState('all')
    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Tobacco', value: 'tobacco' },
        { label: 'Nicotine-Free', value: 'nicotine-free' }
    ]
    const [favorite, setFavorite] = useState<string | null>(null)

    const flavours = product?.items
        ?.filter((item) => item?.ooka_product_option == 'pods')
        ?.sort((a, b) => {
            return a?.required === b?.required ? -1 : 1
        })
    const { flavour, packAmount } = getFlavourAmount(product)
    const { t } = useTranslation(['common', 'plp'])
    const selectedPods: number[] = []
    const selectedOptionsFlavours: BundleItemOption[] = []
    flavours?.forEach((flavour, index) => {
        flavour?.options?.forEach((option) => {
            if (selectedOptions?.includes(String(option?.uid))) {
                selectedOptionsFlavours.push(option!)
                selectedPods.push(index)
            }
        })
    })
    const numberOfAvailablePods = flavours?.length ?? 1

    const mappedFlavours: Mapper[] = aggregateUniqueUIDsBySKU(flavours!)

    const numberOfSelectedFlavours = selectedOptionsFlavours?.length ?? 0

    const requiredFlavours = flavours?.filter((flavour) => flavour?.required)

    const getTitle = (
        numberOfAvailablePods: number,
        requiredFlavoursLength: number
    ) => {
        if (product?.is_flavour_kit) {
            return t('PICK A FLAVOR')
        }
        if (requiredFlavoursLength === numberOfAvailablePods) {
            return t(`PICK {{ numberOfAvailablePods }} FLAVORS`, {
                numberOfAvailablePods: `${numberOfAvailablePods}`
            })
        }
        return requiredFlavoursLength <= 1
            ? t('PICK ANY NUMBER OF FLAVORS')
            : t(`PICK ATLEAST {{requiredFlavoursLength}} FLAVORS`, {
                  requiredFlavoursLength: `{requiredFlavoursLength}`
              })
    }

    const getHeadingforFlavour = () => {
        if (product?.is_flavour_kit) {
            return (
                <p className={fs['sub-pick-any']}>
                    {t(`Your bundle contains 40 pods`)}
                </p>
            )
        } else {
            return (
                <p className={fs['sub-pick-any']}>
                    {t(`Each flavor pack comes with 2 pods`)}
                </p>
            )
        }
    }

    const availableTabs = tabs.filter((tab) => {
        const flavoursForTab = getTabFlavours(mappedFlavours, tab.value)
        return flavoursForTab.length > 0
    })

    /**
     * Combine flavours for each unique product
     */
    return (
        <div>
            <div className={fs['flavour-container']}>
                <p className={fs['pick-any']}>
                    {getTitle(numberOfAvailablePods, requiredFlavours?.length!)}
                </p>
                <p className={fs['sub-pick-any']}>{getHeadingforFlavour()}</p>
                <div className={cn(fs['tabs'])}>
                    {availableTabs.map((tab) => (
                        <span
                            tabIndex={0}
                            role="button"
                            key={tab.value}
                            className={cn(
                                fs['tab'],
                                currentTab === tab.value ? fs['active'] : ''
                            )}
                            onKeyDown={() => {
                                setTab(tab.value)
                            }}
                            onClick={() => {
                                setTab(tab.value)
                            }}
                        >
                            {t(`tabNames|${tab.label}`)}
                        </span>
                    ))}
                </div>
                <div className={cn(fs['flavour-list'])}>
                    {getTabFlavours(mappedFlavours, currentTab)?.map(
                        (option, index) => {
                            const currentFlavour = selectedOptionsFlavours?.filter(
                                (flavourData) => {
                                    return option.uids.includes(flavourData.uid)
                                }
                            )
                            return (
                                <div key={index}>
                                    <FlavoursTile
                                        option={option}
                                        product={product}
                                        className="item"
                                        selectedOptions={selectedOptions}
                                        setSelectedOptions={setSelectedOptions}
                                        selectedFlavours={
                                            selectedOptionsFlavours!
                                        }
                                        qty={currentFlavour?.length}
                                        disableAddingFlavours={
                                            numberOfSelectedFlavours >=
                                                numberOfAvailablePods ||
                                            option.product?.stock_status ===
                                                'OUT_OF_STOCK'
                                        }
                                        isOutOfStock={
                                            option.product?.stock_status ===
                                            'OUT_OF_STOCK'
                                        }
                                        numberOfAvailablePods={
                                            numberOfAvailablePods
                                        }
                                        selectedPods={selectedPods}
                                        isFavourite={isFavourite}
                                        favorite={favorite}
                                        setFavorite={setFavorite}
                                        mappedFlavours={mappedFlavours}
                                        isParentProductIsFlavourKit={
                                            product?.is_flavour_kit
                                        }
                                    />
                                </div>
                            )
                        }
                    )}
                </div>
            </div>
            <FlavourAction
                flavour={flavour}
                packAmount={packAmount}
                product={product}
                selectedOptions={selectedOptions}
                flavours={flavours}
                selectedOptionsFlavours={selectedOptionsFlavours}
                setStep={setStep}
                step={step}
                setSelectedOptions={setSelectedOptions}
                addProductHandler={addProductHandler}
                isAddToCartInProgress={isAddToCartInProgress}
                isFavourite={isFavourite}
            />
        </div>
    )
}

export default FlavoursStep
